import type { Entity, EntityType, Persisted } from '@tunasong/schemas'
import { isEntity, isFolderContainer } from '@tunasong/schemas'
import { defaultSortOptions, type SortOptions } from '../element/index.js'
import type { FilterFunc } from '../filters/filters.js'
import { includeElement } from '../filters/filters.js'
import { entitySearch } from './search.js'
import { elementSort } from './sort.js'

export interface FilteredParams<T extends Persisted<Entity>> {
  entities: T[]
  filter?: EntityType | FilterFunc
  typeFilter?: EntityType[]
  query?: string
  showAll?: boolean
  sortOptions?: SortOptions
  /**
   * List folder containers before everything else, each group in the chosen order, like Finder and Drive. Applies
   * to field sorts only: a manual order (`type: 'order'`) is the user's own arrangement and is left as is.
   */
  foldersFirst?: boolean
}

/** Folder containers first, keeping each group's order */
const pinFolders = <T extends Persisted<Entity>>(entities: T[]) => [
  ...entities.filter(e => isFolderContainer(e)),
  ...entities.filter(e => !isFolderContainer(e)),
]

export const entitySort = <T extends Persisted<Entity> = Persisted<Entity>>({
  entities: allEntities,
  filter = isEntity,
  typeFilter = [],
  query,
  sortOptions = defaultSortOptions,
  foldersFirst = false,
}: FilteredParams<T>): T[] => {
  const entities = allEntities.filter(e => includeElement(e, filter))
  const search = entitySearch(entities)

  /** If query is set, search the songs. If not, just sort them. Filter on the type filter */
  const sorted = elementSort(query ? search(query) : entities, sortOptions).filter(e =>
    typeFilter.length > 0 ? typeFilter.includes(e.type) : true
  ) as T[]
  return foldersFirst && sortOptions.type !== 'order' ? pinFolders(sorted) : sorted
}
